import type { ProgrammaticLandingPageConfig } from '../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from './storageSizeFacts'

export const KWH_5_SPEICHER_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '5-kwh-speicher-kosten',
  lang: 'de',
  topicId: 'kwh-speicher-5',
  title: '5 kWh Batteriespeicher: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet ein 5-kWh-Batteriespeicher? Für wen sich diese Speichergröße eignet, ' +
    'Eigenverbrauchs-Richtwerte und kostenloser Preisvergleich für Anlage + Speicher.',
  keywords: [
    '5 kWh Speicher Kosten',
    'Batteriespeicher 5 kWh Preis',
    '5 kWh Solarspeicher',
    'Was kostet ein 5 kWh Speicher',
  ],
  eyebrow: 'Speichergröße 5 kWh',
  h1: '5 kWh Batteriespeicher: Kosten und Richtwerte',
  intro:
    'Ein 5-kWh-Speicher ist die kleinste unserer indexierten Speicherstufen - eine typische ' +
    `Ergänzung für kleinere Haushalte mit einer ${STORAGE_REFERENCE_KWP}-kWp-Referenzanlage. Hier ` +
    'finden Sie Richtwerte zur passenden Haushaltsgröße, zum Eigenverbrauch mit Speicher und ' +
    'direkt darunter einen Rechner mit dem für diese Kombination vorbelegten Durchschnittspreis ' +
    'aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 5 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich ein 5-kWh-Speicher?' },
    {
      type: 'paragraph',
      text: 'Richtwert: 5 kWh passen typischerweise zu 1-2-Personen-Haushalten mit niedrigem ' +
        'Tagesverbrauch, ohne E-Auto oder Wärmepumpe. Bei höherem ' +
        'Abendverbrauch, Wärmepumpe oder E-Auto lohnt sich ein Blick auf die größeren Speicherstufen.',
    },
    { type: 'heading', text: 'Eigenverbrauch mit Batteriespeicher' },
    {
      type: 'paragraph',
      text:
        `Ohne Speicher liegt der Eigenverbrauchsanteil bei einer ${STORAGE_REFERENCE_KWP}-kWp-Anlage ` +
        `typischerweise bei rund ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Mit Batteriespeicher ` +
        `steigt dieser Anteil im Rechenmodell um weitere ${SELF_CONSUMPTION_BATTERY_BONUS * 100} ` +
        `Prozentpunkte auf rund ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - bei zusätzlicher ` +
        'Wärmepumpe oder E-Auto im Haushalt kann der Anteil noch weiter steigen, bis zu einer ' +
        'Obergrenze von 85 %. Eine genaue, auf Ihren Verbrauch zugeschnittene Berechnung liefert ' +
        'der PV-Rechner.',
    },
    { type: 'heading', text: 'Notstromfähigkeit' },
    {
      type: 'paragraph',
      text:
        'Viele Speichersysteme bieten optional eine Notstromfunktion über eine zusätzliche ' +
        'Backup-Box - bei 5 kWh reicht die Kapazität meist für einzelne zentrale Verbraucher ' +
        '(Kühlschrank, Router, Beleuchtung), nicht für den gesamten Haushalt. Ob und in welchem ' +
        'Umfang ein Angebot Notstromfähigkeit einschließt, ist je Handwerker unterschiedlich - ' +
        'gezielt beim Angebot nachfragen.',
    },
    { type: 'heading', text: 'Speicherkosten und Förderung' },
    {
      type: 'paragraph',
      text:
        `Speicherkosten schwanken je nach Hersteller und Installationsaufwand stark - Marktrecherche ` +
        `2026 zeigt eine Spanne von etwa ${BATTERY_COST_PER_KWH_RANGE_EUR.min} bis ` +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('de-DE')} € pro kWh. Speicherspezifische ` +
        'Förderungen gibt es regional unterschiedlich (z. B. Berliner Landesprogramm mit Zuschuss ' +
        'für Batteriespeicher) - Details im Förder-Überblick, eine genaue Berechnung für Ihre ' +
        'Kombination liefert der Rechner unten.',
    },
  ],
  faq: [
    {
      q: 'Lohnt sich ein 5-kWh-Speicher überhaupt?',
      a: 'Für kleinere Haushalte mit niedrigem Abendverbrauch kann ein 5-kWh-Speicher den Eigenverbrauch spürbar erhöhen. Bei höherem Verbrauch (Wärmepumpe, E-Auto) ist eine größere Stufe meist wirtschaftlicher, siehe die anderen Speichergrößen.',
    },
    {
      q: 'Reicht 5 kWh für Notstrom im ganzen Haus?',
      a: 'In der Regel nicht - 5 kWh eignet sich eher für einzelne zentrale Verbraucher als für die komplette Hausversorgung im Stromausfall. Für längere oder umfassendere Notstromversorgung sind größere Speicherstufen üblich.',
    },
    {
      q: 'Mit welcher Anlagengröße wird ein 5-kWh-Speicher meist kombiniert?',
      a: `Auf dieser Seite als Referenz eine ${STORAGE_REFERENCE_KWP}-kWp-Anlage, weil das die meistgewählte Einfamilienhaus-Größe ist. Bei kleineren Anlagen unter 8 kWp passt 5 kWh oft ebenfalls gut - eine genaue Empfehlung für Ihr Dach liefert der PV-Rechner.`,
    },
    {
      q: 'Reicht 5 kWh, um über Nacht komplett autark zu sein?',
      a: 'Bei sparsamem Verbrauch ohne Wärmepumpe oder E-Auto-Ladung oft ja, bei höherem Verbrauch nur teilweise. Eine genaue, auf Ihren tatsächlichen Verbrauch zugeschnittene Einschätzung liefert der PV-Rechner.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '7 kWh Batteriespeicher: Kosten & Richtwerte', href: '/7-kwh-speicher-kosten' },
    { label: '10 kWh Batteriespeicher: Kosten & Richtwerte', href: '/10-kwh-speicher-kosten' },
    { label: '10 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/10-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
